import { useEffect, useMemo } from 'react';
import { useSocket } from './useSocket';
import { RawEvent, useConnectionData } from '../store';
import { SOCKET_URL } from '../constant';
import { isEmpty } from 'lodash';
import { message } from 'antd';
import * as pako from 'pako';
import { useInitialInfoStore } from '@src/emp-3.0/store/useInitialInfo';

export function useRoomSocket(jwtToken?: string) {
  const { userInfo } = useInitialInfoStore();
  const { setRoomList, setScreenshots, setRawData, setIsConnected, setSocket } =
    useConnectionData();

  const handlers = useMemo(() => {
    const processDecompressedData = (event: ArrayBuffer) => {
      const buffer = new DataView(event);
      let offset = 0;

      // 1. checksum（32 bytes）
      const checksumBytes = new Uint8Array(event, offset, 32);
      const checksumHex = Array.from(checksumBytes)
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
      offset += 32;

      // 2. json length（4 bytes）
      const jsonLength = buffer.getUint32(offset, true); // little-endian
      offset += 4;

      // 3. json data
      const jsonBytes = new Uint8Array(event, offset, jsonLength);
      const jsonString = new TextDecoder().decode(jsonBytes);
      const rawData = JSON.parse(jsonString);
      offset += jsonLength;

      // 4. screenshot length（4 bytes）
      const screenshotLength = buffer.getUint32(offset, false); // big-endian
      offset += 4;

      // 5. screenshot data
      let imageUrl = '';
      if (screenshotLength > 0) {
        const screenshotBytes = new Uint8Array(event, offset, screenshotLength);
        const blob = new Blob([screenshotBytes], { type: 'image/jpeg' });
        imageUrl = URL.createObjectURL(blob);
      }

      // console.log('Received device_data event:', rawData);
      const { eventData, elementsData, timestamp } = rawData;

      if (imageUrl) {
        const screenshot = {
          checksum: checksumHex,
          screenshot: imageUrl,
          elementsData,
          timestamp,
        };
        setScreenshots([screenshot]);
      }

      if (!isEmpty(eventData)) {
        const raw = {
          checksum: checksumHex,
          eventData,
          timestamp,
        } as RawEvent;
        console.log('Received eventData:', raw);
        setRawData(raw);
      }
    };

    return {
      get_rooms: (response: any) => {
        console.log('Received get_rooms event:', response);
        if (response.code === 0) {
          setRoomList(response.data);
        } else {
          message.error(response.message);
        }
      },
      device_data: (event: any) => {
        if (!(event instanceof ArrayBuffer)) {
          console.error('Expected ArrayBuffer');
          return;
        }

        // Check if data is gzip compressed
        const start = performance.now();
        const firstBytes = new Uint8Array(event, 0, 2);
        if (firstBytes[0] === 0x1f && firstBytes[1] === 0x8b) {
          try {
            // Decompress gzip data
            const compressedData = new Uint8Array(event);
            const decompressed = pako.inflate(compressedData);

            const end = performance.now();

            // Now process the decompressed data
            const decompressedBuffer = decompressed.buffer;
            // console.log('Decompression time:', end - start, 'ms');
            // console.log('Original size:', event.byteLength);
            // console.log('Decompressed size:', decompressedBuffer.byteLength);
            // console.log(
            //   'Decompressed rate:',
            //   (event.byteLength / decompressedBuffer.byteLength) * 100,
            //   '%',
            // );

            // Process the decompressed data with original format
            processDecompressedData(decompressedBuffer as ArrayBuffer);
          } catch (error) {
            console.error('Error decompressing gzip data:', error);
          }
        } else {
          // Process uncompressed data directly
          processDecompressedData(event);
        }
      },
    };
  }, [setRawData, setRoomList, setScreenshots]);

  const url = useMemo(
    () =>
      SOCKET_URL.includes('<customer_key>')
        ? SOCKET_URL.replace('<customer_key>', userInfo?.api_key)
        : SOCKET_URL,
    [userInfo],
  );

  const { socket, connected, error, sendMessage, disconnect } = useSocket({
    url,
    handlers,
    jwtToken,
  });

  useEffect(() => {
    setIsConnected(connected);
    setSocket(socket);
  }, [connected, socket]);

  return {
    connected,
    error,
    sendMessage,
    disconnect,
  };
}
